import { observer } from 'mobx-react-lite';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import { MOCK_CHANGEOVER_CREWING, MOCK_CHANGEOVER_SUMMARY } from '../mock/mockData';
import CPMockBadge from './CPMockBadge';
import styles from './cpCalculationTab.module.scss';

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function fmtM(iso: string): string {
  const [year, month] = iso.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('default', { month: 'short', year: '2-digit' });
}

/** Format as 1-decimal float; show '--' for missing values. */
function n1(v: number | undefined | null): string {
  return v === undefined || v === null ? '--' : v.toFixed(1);
}

/** Format as rounded integer with locale separators; show '--' for missing. */
function n0(v: number | undefined | null): string {
  return v === undefined || v === null ? '--' : Math.round(v).toLocaleString();
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Display order for PPS room types — exactly as named in the system. */
const ROOM_TYPES: string[] = [
  'High Speed 1',
  'High Speed 2',
  'Medium Speed',
  'Low Speed 1',
  'Low Speed 2',
  'Overlay',
];

/** Room types that belong to each CO category (used to expand HIGH/LOW/OVER to individual types). */
const CAT_ROOM_TYPES: Record<string, string[]> = {
  HIGH: ['High Speed 1', 'High Speed 2', 'Medium Speed'],
  LOW:  ['Low Speed 1', 'Low Speed 2'],
  OVER: ['Overlay'],
};

/** Expanded CO keys: 3 levels × 6 room types = 18 entries. */
const CO_ROOM_TYPE_KEYS: Array<{
  level: 'L1' | 'L2' | 'L3';
  roomType: string;
  cat: 'HIGH' | 'LOW' | 'OVER';
}> = [];
(['L1', 'L2', 'L3'] as const).forEach((level) => {
  (['HIGH', 'LOW', 'OVER'] as const).forEach((cat) => {
    CAT_ROOM_TYPES[cat].forEach((roomType) => {
      CO_ROOM_TYPE_KEYS.push({ level, roomType, cat });
    });
  });
});

// ---------------------------------------------------------------------------
// Row type for the ResPlanCalc table
// ---------------------------------------------------------------------------

type RowKind =
  | 'groupHeader'   // section header row — blue-grey band, no values
  | 'data'          // standard indented data row
  | 'subtotal'      // green-tinted summary row
  | 'total'         // dark total row
  | 'fixed'         // italic fixed-staff row
  | 'pct';          // percentage-formatted value row

interface CalcRow {
  label: string;
  kind: RowKind;
  /** Compute value for month at index i — called with the month string. */
  getValue: (month: string, idx: number) => number | undefined | null;
  fmt?: 'n0' | 'n1' | 'pct';  // default n1
}

// ---------------------------------------------------------------------------
// Pre-compute CO type proportions from MOCK_CHANGEOVER_SUMMARY
// ---------------------------------------------------------------------------

const coCatTotalMins: Record<string, number> = { HIGH: 0, LOW: 0, OVER: 0 };
MOCK_CHANGEOVER_SUMMARY.forEach((r) => {
  coCatTotalMins[r.room_category] = (coCatTotalMins[r.room_category] ?? 0) + r.total_r2_minutes;
});

const coTypeProportion: Record<string, number> = {};
MOCK_CHANGEOVER_SUMMARY.forEach((r) => {
  const key = `${r.clean_level}_${r.room_category}`;
  coTypeProportion[key] =
    coCatTotalMins[r.room_category] > 0
      ? r.total_r2_minutes / coCatTotalMins[r.room_category]
      : 0;
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CPCalculationTab = observer(() => {
  const store = useCapacityStore();
  const { dashboardData } = store;

  if (!dashboardData) {
    if (store.isDashboardLoading) {
      return <div className={styles.empty}><i className="pi pi-spin pi-spinner" style={{ marginRight: 8 }} />Loading…</div>;
    }
    return (
      <div className={styles.noData}>
        <i className="pi pi-calculator" />
        <p>No calculation data yet. Set your planning parameters and click <strong>Generate Dashboard</strong>.</p>
      </div>
    );
  }

  const {
    months,
    rooms,
    capacity_by_room_type,
    changeover_data,
    forecast_jobs,
    hc_breakdown,
    crewing_factors,
    summary,
  } = dashboardData;

  const downtime = summary.planning_params['downtime_factor'] ?? 0.20;

  // Helper: sum pa_hc / mo_hc / qi_hc across all rooms of a given room_type for a month.
  function roomTypeHcSum(
    roomType: string,
    month: string,
    field: 'pa_hc' | 'mo_hc' | 'qi_hc',
  ): number {
    return rooms
      .filter((r) => r.room_type === roomType)
      .reduce((acc, r) => acc + (r.months[month]?.[field] ?? 0), 0);
  }

  // Helper: CO monthly hours for one level+category combination (L1_HIGH total, etc.)
  function coTypeMonthlyHrs(month: string, level: 'L1' | 'L2' | 'L3', cat: 'HIGH' | 'LOW' | 'OVER'): number {
    const catHrs = changeover_data[cat]?.[month] ?? 0;
    return catHrs * (coTypeProportion[`${level}_${cat}`] ?? 0);
  }

  // Helper: CO monthly hours split to an individual room type (within a category).
  // Proportional by that month's line hours for the room types in the category.
  function coRoomTypeMonthlyHrs(
    month: string,
    level: 'L1' | 'L2' | 'L3',
    cat: 'HIGH' | 'LOW' | 'OVER',
    roomType: string,
  ): number {
    const typeHrs = coTypeMonthlyHrs(month, level, cat);
    const catRoomTypes = CAT_ROOM_TYPES[cat];
    if (catRoomTypes.length === 1) return typeHrs; // Overlay — no split needed
    const totalLineHrs = catRoomTypes.reduce(
      (s, rt) => s + (capacity_by_room_type[rt]?.[month] ?? 0), 0,
    );
    if (totalLineHrs === 0) return typeHrs / catRoomTypes.length; // equal split fallback
    const roomLineHrs = capacity_by_room_type[roomType]?.[month] ?? 0;
    return typeHrs * (roomLineHrs / totalLineHrs);
  }

  // Pre-compute section C sums (used again in G).
  function inRunPaHrs(month: string): number {
    return ROOM_TYPES.reduce((s, rt) => s + roomTypeHcSum(rt, month, 'pa_hc'), 0);
  }
  function inRunMoHrs(month: string): number {
    return ROOM_TYPES.reduce((s, rt) => s + roomTypeHcSum(rt, month, 'mo_hc'), 0);
  }
  function inRunQiHrs(month: string): number {
    return ROOM_TYPES.reduce((s, rt) => s + roomTypeHcSum(rt, month, 'qi_hc'), 0);
  }

  // Pre-compute section F sums (used again in G) — summed across all 18 room-type CO entries.
  function coPaHrs(month: string): number {
    return CO_ROOM_TYPE_KEYS.reduce((s, { level, roomType, cat }) => {
      const crewing = MOCK_CHANGEOVER_CREWING[`${level}_${roomType}`];
      return s + coRoomTypeMonthlyHrs(month, level, cat, roomType) * (crewing?.PA ?? 0);
    }, 0);
  }
  function coMoHrs(month: string): number {
    return CO_ROOM_TYPE_KEYS.reduce((s, { level, roomType, cat }) => {
      const crewing = MOCK_CHANGEOVER_CREWING[`${level}_${roomType}`];
      return s + coRoomTypeMonthlyHrs(month, level, cat, roomType) * (crewing?.MO ?? 0);
    }, 0);
  }
  function coQiHrs(month: string): number {
    return CO_ROOM_TYPE_KEYS.reduce((s, { level, roomType, cat }) => {
      const crewing = MOCK_CHANGEOVER_CREWING[`${level}_${roomType}`];
      return s + coRoomTypeMonthlyHrs(month, level, cat, roomType) * (crewing?.QI ?? 0);
    }, 0);
  }

  // -------------------------------------------------------------------------
  // Build the full row list exactly matching the Excel ResPlanCalc sheet order
  // -------------------------------------------------------------------------

  const calcRows: CalcRow[] = [];

  // ---- Section A: IN RUN VARIABLES ----------------------------------------

  calcRows.push({
    label: 'IN RUN VARIABLES',
    kind: 'groupHeader',
    getValue: () => undefined,
  });

  calcRows.push({
    label: 'Total Bottles',
    kind: 'data',
    fmt: 'n0',
    getValue: (m) => crewing_factors[m]?.forecast_bottles,
  });

  calcRows.push({
    label: 'Total Line Hours',
    kind: 'data',
    getValue: (m) =>
      ROOM_TYPES.reduce((s, rt) => s + (capacity_by_room_type[rt]?.[m] ?? 0), 0),
  });

  ROOM_TYPES.forEach((rt) => {
    calcRows.push({
      label: `${rt} Line Hrs`,
      kind: 'data',
      getValue: (m) => capacity_by_room_type[rt]?.[m],
    });
  });

  // ---- Section B: IN RUN LABOR HOURS (6 room types × PA/MO/QI = 18 rows) --

  calcRows.push({
    label: 'IN RUN LABOR HOURS',
    kind: 'groupHeader',
    getValue: () => undefined,
  });

  ROOM_TYPES.forEach((rt) => {
    calcRows.push({
      label: `${rt} PA Hrs`,
      kind: 'data',
      getValue: (m) => roomTypeHcSum(rt, m, 'pa_hc'),
    });
    calcRows.push({
      label: `${rt} MO Hrs`,
      kind: 'data',
      getValue: (m) => roomTypeHcSum(rt, m, 'mo_hc'),
    });
    calcRows.push({
      label: `${rt} QI Hrs`,
      kind: 'data',
      getValue: (m) => roomTypeHcSum(rt, m, 'qi_hc'),
    });
  });

  // ---- Section C: IN RUN LABOR SUMMARY ------------------------------------

  calcRows.push({
    label: 'IN RUN LABOR SUMMARY',
    kind: 'groupHeader',
    getValue: () => undefined,
  });

  calcRows.push({
    label: 'In-Run PA Hrs',
    kind: 'subtotal',
    getValue: (m) => inRunPaHrs(m),
  });

  calcRows.push({
    label: 'In-Run MO Hrs',
    kind: 'subtotal',
    getValue: (m) => inRunMoHrs(m),
  });

  calcRows.push({
    label: 'In-Run QI Hrs',
    kind: 'subtotal',
    getValue: (m) => inRunQiHrs(m),
  });

  // ---- Section D: CHANGEOVER VARIABLES ------------------------------------

  calcRows.push({
    label: 'CHANGEOVER VARIABLES',
    kind: 'groupHeader',
    getValue: () => undefined,
  });

  calcRows.push({
    label: 'Monthly Jobs',
    kind: 'data',
    getValue: (m) => forecast_jobs[m],
  });

  calcRows.push({
    label: 'Total CO Hrs',
    kind: 'data',
    getValue: (m) =>
      (changeover_data['HIGH']?.[m] ?? 0) +
      (changeover_data['LOW']?.[m] ?? 0) +
      (changeover_data['OVER']?.[m] ?? 0),
  });

  CO_ROOM_TYPE_KEYS.forEach(({ level, roomType, cat: _cat }) => {
    calcRows.push({
      label: `${level} ${roomType} CO Hrs`,
      kind: 'data',
      getValue: (m) => coRoomTypeMonthlyHrs(m, level, _cat, roomType),
    });
  });

  // ---- Section E: CHANGEOVER LABOR HOURS (9 CO types × PA/MO/QI = 27 rows)

  calcRows.push({
    label: 'CHANGEOVER LABOR HOURS',
    kind: 'groupHeader',
    getValue: () => undefined,
  });

  CO_ROOM_TYPE_KEYS.forEach(({ level, roomType, cat }) => {
    const crewing = MOCK_CHANGEOVER_CREWING[`${level}_${roomType}`];
    calcRows.push({
      label: `${level} ${roomType} PA Hrs`,
      kind: 'data',
      getValue: (m) => coRoomTypeMonthlyHrs(m, level, cat, roomType) * (crewing?.PA ?? 0),
    });
    calcRows.push({
      label: `${level} ${roomType} MO Hrs`,
      kind: 'data',
      getValue: (m) => coRoomTypeMonthlyHrs(m, level, cat, roomType) * (crewing?.MO ?? 0),
    });
    calcRows.push({
      label: `${level} ${roomType} QI Hrs`,
      kind: 'data',
      getValue: (m) => coRoomTypeMonthlyHrs(m, level, cat, roomType) * (crewing?.QI ?? 0),
    });
  });

  // ---- Section F: CHANGEOVER LABOR SUMMARY --------------------------------

  calcRows.push({
    label: 'CHANGEOVER LABOR SUMMARY',
    kind: 'groupHeader',
    getValue: () => undefined,
  });

  calcRows.push({
    label: 'CO PA Hrs',
    kind: 'subtotal',
    getValue: (m) => coPaHrs(m),
  });

  calcRows.push({
    label: 'CO MO Hrs',
    kind: 'subtotal',
    getValue: (m) => coMoHrs(m),
  });

  calcRows.push({
    label: 'CO QI Hrs',
    kind: 'subtotal',
    getValue: (m) => coQiHrs(m),
  });

  // ---- Section G: AGGREGATE LABOR SUMMARY ---------------------------------

  calcRows.push({
    label: 'AGGREGATE LABOR SUMMARY',
    kind: 'groupHeader',
    getValue: () => undefined,
  });

  calcRows.push({
    label: 'Total PA Hrs',
    kind: 'subtotal',
    getValue: (m) => (inRunPaHrs(m) + coPaHrs(m)) * (1 + downtime),
  });

  calcRows.push({
    label: 'Total MO Hrs',
    kind: 'subtotal',
    getValue: (m) => (inRunMoHrs(m) + coMoHrs(m)) * (1 + downtime),
  });

  calcRows.push({
    label: 'Total QI Hrs',
    kind: 'subtotal',
    getValue: (m) => (inRunQiHrs(m) + coQiHrs(m)) * (1 + downtime),
  });

  calcRows.push({
    label: 'Work Days',
    kind: 'data',
    fmt: 'n0',
    getValue: (m) => crewing_factors[m]?.work_days,
  });

  calcRows.push({
    label: 'Hours / Shift',
    kind: 'data',
    fmt: 'n0',
    getValue: () => 8,
  });

  calcRows.push({
    label: 'Downtime Factor',
    kind: 'pct',
    fmt: 'pct',
    getValue: () => downtime,
  });

  calcRows.push({
    label: 'PA Headcount',
    kind: 'subtotal',
    fmt: 'n0',
    getValue: (m) => {
      const workDays = crewing_factors[m]?.work_days ?? 0;
      if (workDays === 0) return undefined;
      return Math.ceil((inRunPaHrs(m) + coPaHrs(m)) * (1 + downtime) / (workDays * 8));
    },
  });

  calcRows.push({
    label: 'MO Headcount',
    kind: 'subtotal',
    fmt: 'n0',
    getValue: (m) => {
      const workDays = crewing_factors[m]?.work_days ?? 0;
      if (workDays === 0) return undefined;
      return Math.ceil((inRunMoHrs(m) + coMoHrs(m)) * (1 + downtime) / (workDays * 8));
    },
  });

  calcRows.push({
    label: 'QI Headcount',
    kind: 'subtotal',
    fmt: 'n0',
    getValue: (m) => {
      const workDays = crewing_factors[m]?.work_days ?? 0;
      if (workDays === 0) return undefined;
      return Math.ceil((inRunQiHrs(m) + coQiHrs(m)) * (1 + downtime) / (workDays * 8));
    },
  });

  // ---- Section H: FIXED STAFF ---------------------------------------------

  calcRows.push({
    label: 'FIXED STAFF',
    kind: 'groupHeader',
    getValue: () => undefined,
  });

  calcRows.push({
    label: 'PA Lead (fixed)',
    kind: 'fixed',
    getValue: (m) => hc_breakdown[m]?.pa_lead,
  });

  calcRows.push({
    label: 'MO Lead (fixed)',
    kind: 'fixed',
    getValue: (m) => hc_breakdown[m]?.mo_lead,
  });

  calcRows.push({
    label: 'QI Lead (fixed)',
    kind: 'fixed',
    getValue: (m) => hc_breakdown[m]?.qi_lead,
  });

  calcRows.push({
    label: 'Aggregation',
    kind: 'fixed',
    getValue: (m) => hc_breakdown[m]?.aggregation,
  });

  calcRows.push({
    label: 'Shift Coverage',
    kind: 'fixed',
    getValue: (m) => hc_breakdown[m]?.shift_coverage,
  });

  calcRows.push({
    label: 'QLS',
    kind: 'fixed',
    getValue: (m) => hc_breakdown[m]?.qls,
  });

  // ---- Section I: FINAL TOTALS --------------------------------------------

  calcRows.push({
    label: 'FINAL TOTALS',
    kind: 'groupHeader',
    getValue: () => undefined,
  });

  calcRows.push({
    label: 'Packaging Total HC',
    kind: 'subtotal',
    getValue: (m) => hc_breakdown[m]?.packaging_total,
  });

  calcRows.push({
    label: 'Quality Total HC',
    kind: 'subtotal',
    getValue: (m) => hc_breakdown[m]?.quality_total,
  });

  calcRows.push({
    label: 'Grand Total HC',
    kind: 'total',
    getValue: (m) => {
      const pt = hc_breakdown[m]?.packaging_total ?? 0;
      const qt = hc_breakdown[m]?.quality_total ?? 0;
      return pt + qt;
    },
  });

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  function formatCell(row: CalcRow, value: number | undefined | null): string {
    if (row.fmt === 'n0') return n0(value);
    if (row.fmt === 'pct') {
      return value === undefined || value === null
        ? '--'
        : `${(value * 100).toFixed(1)}%`;
    }
    return n1(value);
  }

  function rowClassName(kind: RowKind): string | undefined {
    switch (kind) {
      case 'groupHeader': return styles.rowGroupHeader;
      case 'subtotal':    return styles.rowSubtotal;
      case 'total':       return styles.rowTotal;
      case 'fixed':       return styles.rowFixed;
      default:            return undefined;
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className={styles.wrapper}>
      <div className={styles.pageHeader}>
        <i className="pi pi-calculator" />
        <h2>Calculation Detail</h2>
        <CPMockBadge />
      </div>

      <div className={styles.tabContainer}>
        <div className={styles.scrollOuter}>
          <table className={styles.calcTable}>
            <thead>
              <tr>
                <th className={styles.labelColHeader}>Row</th>
                {months.map((m) => (
                  <th key={m}>{fmtM(m)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {calcRows.map((row) => {
                const cls = rowClassName(row.kind);
                const isHeader = row.kind === 'groupHeader';
                return (
                  <tr key={row.label} className={cls}>
                    <td
                      className={
                        isHeader
                          ? styles.labelCol
                          : `${styles.labelCol} ${styles.labelIndent}`
                      }
                    >
                      {row.label}
                    </td>
                    {months.map((m, idx) => (
                      <td
                        key={idx}
                        className={styles.numCell}
                      >
                        {isHeader ? '' : formatCell(row, row.getValue(m, idx))}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

export default CPCalculationTab;
